import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ReportQuestionPopup, StickyMenu, FAQ, ConfirmationScreen, AbandonedScreen, CompletedScreen, AbandonConfirmationModal, ConfirmationModal, CompletionProgressBar, StatusPill, TimeExpiredBox, Footer } from '../ui';
import { AntiCheating } from '../utils';
import supabase from '../utils/SupabaseConfig';
import { useSession } from '../../context/SessionContext';
import { useNavigation } from '../../hooks/useNavigation';
import { QUESTIONNAIRE_ENDPOINTS, OTHER_ENDPOINTS } from '../../constants/apiEndpoints';
import { useCheatingDetection, useTimer, useReportPopup, useAbandonConfirmation } from '../../hooks';
import { CompletionCircle, ElapsedTime } from './components/ChallengeInterview';
import {
  MultiOptionsQuestion,
  SingleOptionQuestion,
  TrueFalseQuestion,
  LongTextQuestion,
  ShortTextQuestion,
  AudioAnswerQuestionWithSave,
  VideoAnswerQuestionWithSave
} from '../questions';
import { getQuestionTypeLabel } from '../questions/utils/questionHelpers';

function Questionnaire() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { sessionData, updateSessionData } = useSession();
  const { navigatePrevious } = useNavigation();
  
  // Get eid from URL path parameter
  const eid = params.eid;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questionnaireInstanceId, setQuestionnaireInstanceId] = useState(null); // Store the questionnaire instance ID from the server
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false); // State for submit confirmation modal
  // No need to track last save time or updates
  const { reportPopupOpen, currentQuestionId, currentQuestion, openReportPopup, closeReportPopup } = useReportPopup();

  // Helper function to format responses for submission - memoized with useCallback
  const formatResponses = useCallback(() => {
    if (!data || !data.questions) return [];
    
    // Create an array of question answers in the structured format
    return data.questions.map(question => {
      const response = responses[question.id];
      
      // For single-answer and multiple-answer questions, include otherAnswer field
      if (question.type === 'single-answer' || question.type === 'multiple-answer') {
        return {
          id: question.id,
          answer: response?.answer || (question.type === 'multiple-answer' ? [] : ''),
          otherAnswer: response?.otherAnswer || ''
        };
      }
      // For audio-answer questions, flatten the structure
      else if (question.type === 'audio-answer' && response && typeof response === 'object') {
        return {
          id: question.id,
          answer: response.answer || '',
          file_id: response.file_id,
          file_key: response.file_key
        };
      }
      // For video-answer questions, flatten the structure
      else if (question.type === 'video-answer' && response && typeof response === 'object') {
        return {
          id: question.id,
          answer: response.answer || '',
          file_id: response.file_id,
          file_key: response.file_key
        };
      }
      // For other question types, keep the original format
      else {
        return {
          id: question.id,
          answer: response || ''
        };
      }
    });
  }, [data, responses]);

  // Submit questionnaire function
  const submitQuestionnaire = async () => {
    setIsSubmitting(true);
    setShowSubmitConfirm(false); // Close the confirmation modal

    if (data && data.questions) {
      // Get formatted question answers
      const questionAnswers = formatResponses();
      
      try {
        // Create the data structure to send to the server
        const dataToSend = {
          qi_id: questionnaireInstanceId,
          questionAnswers: questionAnswers,
          cheatingEvents: [...cheatingEvents] // Include cheating events in the data sent to the server
        };
        
        // Log the data being submitted to the server
        console.log('Submitting Questionnaire Data:', JSON.stringify(dataToSend, null, 2));
        
        // Send the data to the webhook
        const response = await fetch(QUESTIONNAIRE_ENDPOINTS.SAVE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend),
        });

        if (!response.ok) {
          throw new Error(`Failed to save data: ${response.status} ${response.statusText}`);
        }
        
        // Parse the response to check the status
        const responseData = await response.json();
        
        // Check if the response contains the expected status using optional chaining
        if (responseData?.[0]?.questionnaire_status !== 'qs_done') {
          throw new Error('Server did not confirm questionnaire submission. Expected status: qs_done');
        }
        
        // Log successful submission
        console.log('Questionnaire submitted successfully!', new Date().toISOString());
        
        // Update local state and session data to mark questionnaire as completed with the status from the server
        setQuestionnaireStatus(responseData[0].questionnaire_status);
        updateSessionData({ questionnaire_status: responseData[0].questionnaire_status });
        
        // Reset submission state
        setIsSubmitting(false);
        
        // Automatically navigate to Challenge
        navigate(`/challenge/${eid}`);
        // Scroll to top after navigation
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      } catch (err) {
        console.error('Error saving questionnaire data:', err);
        // Log detailed error information for debugging
        console.log('Submission Error Details:', {
          timestamp: new Date().toISOString(),
          errorMessage: err.message,
          responseData: responses,
          eid: eid
        });
        
        setError(err.message);
        setIsSubmitting(false);
        
        // Show user-friendly error message
        alert("There was an error submitting your questionnaire. Your responses are still saved. Please try submitting again.");
      }
    }
  };
  
  const [startDate, setStartDate] = useState(null);
  const [timerDuration, setTimerDuration] = useState(null);
  const { timerExpired, handleTimeUp } = useTimer();
  const { cheatingEvents, handleCheatingDetected, cleanup } = useCheatingDetection();

  // State to track questionnaire status
  const [questionnaireStatus, setQuestionnaireStatus] = useState(sessionData?.questionnaire_status || 'qs_not_started');
  
  // Function to save responses to Supabase - memoized with useCallback to capture latest responses
  const saveResponsesToSupabase = useCallback(async () => {
    // Only save if questionnaire is started, we have an ID, and there are responses to save
    if (questionnaireStatus !== 'qs_started' || !questionnaireInstanceId || !Object.keys(responses).length) {
      return;
    }

    try {
      // Get formatted question answers
      const questionAnswers = formatResponses();
      
      console.log("questionAnswers to save: ", questionAnswers);

      // Update the record in Supabase
      const { data, error } = await supabase
        .from('questionnaire_instance')
        .update({ questionnaire_response_json: questionAnswers })
        .eq('qi_id', questionnaireInstanceId);

      if (error) {
        console.error('Error saving to Supabase:', error);
      } else {
        console.log('Successfully saved to Supabase:', new Date().toISOString());
      }
    } catch (err) {
      console.error('Exception saving to Supabase:', err);
    }
  }, [questionnaireStatus, questionnaireInstanceId, responses, formatResponses]);

  // Auto-save responses to Supabase every X seconds
  useEffect(() => {
    // Only set up auto-save if questionnaire is started
    if (questionnaireStatus !== 'qs_started') {
      return;
    }
    
    // Set up interval to save responses
    const autoSaveInterval = setInterval(saveResponsesToSupabase, 30000); // Save every X seconds

    // Clean up interval on unmount
    return () => clearInterval(autoSaveInterval);
  }, [questionnaireStatus, saveResponsesToSupabase]); // Include saveResponsesToSupabase in dependencies

  useEffect(() => {
    const fetchData = async () => {
      // Skip if no eid
      if (!eid) return;
      
      try {
        const response = await fetch(`${QUESTIONNAIRE_ENDPOINTS.GET}?eid=${eid}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log("Questionnaire responseData:", responseData);

        // Get the questionnaire response data with optional chaining
        const questionnaireResponse = responseData?.[0];
        if (!questionnaireResponse) {
          throw new Error('No questionnaire data found in response');
        }
        
        // Set status first - this is important for UI rendering
        setQuestionnaireStatus(questionnaireResponse.questionnaire_status);
        updateSessionData({ questionnaire_status: questionnaireResponse.questionnaire_status });
        
        // Set other basic data
        setQuestionnaireInstanceId(questionnaireResponse.qi_id);
        setStartDate(questionnaireResponse.questionnaire_start_date);
        setTimerDuration(questionnaireResponse.max_duration_minutes);
        
        // Only try to set questionnaire data if it exists
        // This handles the abandoned case where questionnaire data might not be present
        if (questionnaireResponse.questionnaire) {
          setData(questionnaireResponse.questionnaire);
          
          // If there's autosaved response data, prefill the form with it
          if (questionnaireResponse.questionnaire_response_json?.length > 0) {
            console.log("Prefilling form with autosaved data:", questionnaireResponse.questionnaire_response_json);
            
            // Convert the array of question answers to an object keyed by question id
            const prefillResponses = {};
            
            // Process each item
            for (const item of questionnaireResponse.questionnaire_response_json) {
              if (item && item.id) {
                // For audio-answer questions, fetch signed URL
                if (item.file_id && item.file_key) {
                  try {
                    const response = await fetch(OTHER_ENDPOINTS.SIGN_URL, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ file_key: item.file_key })
                    });
                    
                    const data = await response.json();
                    
                    prefillResponses[item.id] = {
                      answer: item.answer,
                      file_id: item.file_id,
                      file_key: item.file_key,
                      url: data[0]?.signed_url
                    };
                  } catch (error) {
                    console.error('Error fetching signed URL:', error);
                    prefillResponses[item.id] = {
                      answer: item.answer,
                      file_id: item.file_id,
                      file_key: item.file_key
                    };
                  }
                }
                // For single-answer and multiple-answer questions, preserve the otherAnswer field if it exists
                else if (item.otherAnswer !== undefined) {
                  prefillResponses[item.id] = {
                    answer: item.answer,
                    otherAnswer: item.otherAnswer
                  };
                } 
                // For other question types
                else {
                  prefillResponses[item.id] = item.answer;
                }
              }
            }
            
            // Set the responses state with the prefilled data
            setResponses(prefillResponses);
          }
        }
      } catch (err) {
        console.error('Error fetching questionnaire:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eid, sessionData?.questionnaire_status]);

  // Cleanup effect to clear pending timeouts when component unmounts
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const { 
    showAbandonConfirm, 
    handleAbandonClick, 
    handleCancelAbandon, 
    handleConfirmAbandon 
  } = useAbandonConfirmation(questionnaireInstanceId, eid, QUESTIONNAIRE_ENDPOINTS.ABANDON, 'questionnaire_status', 'qs_abandoned');
  
  const handlePrevious = () => {
    navigatePrevious(location.pathname);
  };
  
  const handleChange = (questionId, value) => {
    // If timer has expired, don't allow changes
    if (timerExpired) return;
    
    // Update responses using functional update pattern
    setResponses(prev => {
      const updated = { ...prev, [questionId]: value };
      console.log("Updated responses:", updated);
      return updated;
    });
    
    // No immediate save - will be saved on the next interval
  };

  // Function to start the questionnaire
  const startQuestionnaire = async () => {
    setLoading(true);
    try {      
      // Send request to start the questionnaire
      const response = await fetch(`${QUESTIONNAIRE_ENDPOINTS.START}?qi_id=${questionnaireInstanceId}`);

      if (!response.ok) {
        throw new Error(`Failed to start questionnaire: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      const startResponse = responseData[0];
      
      setStartDate(startResponse.questionnaire_start_date);
      setQuestionnaireStatus(startResponse.questionnaire_status);
      
      // Update session data with the new status
      updateSessionData({ 
        questionnaire_status: startResponse.questionnaire_status
      });
    } catch (err) {
      console.error('Error starting questionnaire:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate the number of answered questions
  const getAnsweredQuestionsCount = () => {
    if (!data || !data.questions) return 0;
    
    // Count questions that have a non-empty response
    return data.questions.reduce((count, question) => {
      const response = responses[question.id];
      
      // For multiple-answer questions, check if the array has items
      if (question.type === 'multiple-answer') {
        return response && response.answer && response.answer.length > 0 ? count + 1 : count;
      }
      
      // For single-answer questions with otherAnswer field
      if (question.type === 'single-answer') {
        return (response && (response.answer || response.otherAnswer)) ? count + 1 : count;
      }
      
      // For audio-answer questions, check if file_key exists
      if (question.type === 'audio-answer') {
        return (response && typeof response === 'object' && response.file_key) ? count + 1 : count;
      }
      
      // For video-answer questions, check if file_key exists
      if (question.type === 'video-answer') {
        return (response && typeof response === 'object' && response.file_key) ? count + 1 : count;
      }
      
      // For other question types, check if response exists and is not empty
      return response && response !== '' ? count + 1 : count;
    }, 0);
  };
  
  // Get the count of answered questions
  const answeredQuestionsCount = getAnsweredQuestionsCount();
  
  // Check if both questionnaire_status and challenge_status are "Done"
  useEffect(() => {
    if (sessionData?.questionnaire_status === 'qs_done' && sessionData?.challenge_status === 'cs_done') {
      navigate(`/finished/${eid}`);
    }
  }, [sessionData, navigate, eid]);

  return (
    <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
      {/* Sticky Top Menu with Countdown Timer (only shown when active) */}
      <StickyMenu 
        startDate={questionnaireStatus === 'qs_started' ? startDate : null}
        timerDuration={questionnaireStatus === 'qs_started' ? timerDuration : null}
        onTimeUp={handleTimeUp}
      />
      
      {/* Time's Up box - only shown when timer has expired */}
      <TimeExpiredBox 
        isVisible={timerExpired} 
        onSubmit={() => document.getElementById('submit-questionnaire-button').click()} 
        buttonText="Submit Questionnaire"
      />
      
      {/* 2-Column Layout Container */}
      <div className="flex-1 flex justify-center px-4">
        <div className={`w-full bg-white rounded-3xl shadow-md flex ${
          questionnaireStatus === 'qs_started' ? 'max-w-[1100px]' : 'max-w-[800px]'
        }`}>
          {/* Main Content */}
          <div className={`flex-1 py-12 px-8 flex flex-col items-center relative ${
            questionnaireStatus === 'qs_started' ? 'border-r border-gray-200' : ''
          }`}>
            {/* Status Pill - only show when not started or in other non-active states */}
            {questionnaireStatus !== 'qs_started' && (
              <div className="absolute top-4 right-4">
                <StatusPill status={questionnaireStatus} />
              </div>
            )}
            
            {/* Show appropriate header based on questionnaire status */}
            {questionnaireStatus === 'qs_not_started' ? (
              /* Simple title for not started state */
              <div className="relative w-full mb-4">
                <h2 className="text-3xl font-semibold text-center">Questionnaire</h2>
              </div>
            ) : questionnaireStatus === 'qs_started' ? (
              /* Simple header when questionnaire is active */
              <h2 className="text-3xl font-semibold mb-8 text-center">Questionnaire</h2>
            ) : null}
        
        {/* Anti-cheating component */}
        <AntiCheating onCheatingDetected={handleCheatingDetected} />
        
        {/* Report Question Popup */}
        <ReportQuestionPopup 
          isOpen={reportPopupOpen} 
          onClose={closeReportPopup} 
          questionId={currentQuestionId}
          eval_id={eid}
          type="Questionnaire"
          question={currentQuestion}
        />
        
        {/* If questionnaire is not started, show confirmation screen */}
        {questionnaireStatus === 'qs_not_started' && !loading ? (
          <ConfirmationScreen
            title="Ready To Start?"
            description="Once started, please complete the questionnaire in one session." // Once started you must complete the questionnaire in one session. If the time runs out, your application will expire.
            timeRequired={`${timerDuration} minutes`}
            onStart={startQuestionnaire}
            onBack={handlePrevious}
            startButtonText="Start Questionnaire"
            backButtonText="Back"
            icon="question"
            faqType="questionnaire"
          />
        ) : questionnaireStatus === 'qs_abandoned' ? (
          // NOTE: This AbandonedScreen is currently unreachable because WelcomeScreen.js 
          // handles all abandoned states globally. To reach this code, you would need to:
          // 1. Comment out the abandoned check in WelcomeScreen.js
          // 2. Remove the abandoned redirect restriction in ProtectedRoute.js
          <AbandonedScreen
            title="Questionnaire Expired"
            message="This questionnaire has expired or was abandoned."
            onBack={() => navigate(`/${eid}`)}
            backButtonText="Start Page"
          />
        ) : questionnaireStatus === 'qs_done' ? (
          <CompletedScreen
            title="Questionnaire Completed!"
            message="Thank you for completing this questionnaire. Please proceed to the next section."
            onContinue={() => {
              navigate(`/challenge/${eid}`);
              // Scroll to top after navigation
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 0);
            }}
            continueButtonText="Continue to Challenge"
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 mb-4" style={{ borderTopColor: '#33C6B6', borderBottomColor: '#33C6B6' }}></div>
            <div className="text-xl font-medium" style={{ color: '#33C6B6' }}>Loading questionnaire...</div>
          </div>
        ) : error ? (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md w-full">
            <div className="flex items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="font-bold">Error</p>
            </div>
            <p>{error}</p>
          </div>
        ) : (
          <form className="w-full">
          {data.questions.map((question, index) => (
            <div key={question.id} className="mb-10 pb-8 border-b border-gray-200 transition-all duration-300 hover:shadow-md hover:bg-gray-50 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <div className="text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 shadow-sm" style={{ backgroundColor: '#33C6B6' }}>
                    <span className="font-bold">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {getQuestionTypeLabel(question.type)}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    openReportPopup(question.id, question);
                  }}
                  className="text-sm bg-white hover:bg-gray-50 text-black border border-black px-3 py-1 rounded-full flex items-center transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Report
                </button>
              </div>
              <p className="text-lg mb-4 text-gray-700 font-medium">{question.questionText}</p>
              <div className="mt-4 pl-4 border-l-4 border-blue-200 py-1">
                {question.type === 'multiple-answer' && (
                  <MultiOptionsQuestion 
                    question={question} 
                    value={responses[question.id] || []} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                  />
                )}
                {question.type === 'single-answer' && (
                  <SingleOptionQuestion 
                    question={question} 
                    value={responses[question.id] || ''} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                  />
                )}
                {question.type === 'long-text' && (
                  <LongTextQuestion 
                    question={question} 
                    value={responses[question.id] || ''} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                  />
                )}
                {question.type === 'short-text' && (
                  <ShortTextQuestion 
                    question={question} 
                    value={responses[question.id] || ''} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                  />
                )}
                {question.type === 'true-false' && (
                  <TrueFalseQuestion 
                    question={question} 
                    value={responses[question.id] || ''} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                  />
                )}
                {question.type === 'audio-answer' && (
                  <AudioAnswerQuestionWithSave 
                    question={question} 
                    value={responses[question.id] || ''} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                    questionnaireInstanceId={questionnaireInstanceId}
                  />
                )}
                {question.type === 'video-answer' && (
                  <VideoAnswerQuestionWithSave 
                    question={question} 
                    value={responses[question.id] || ''} 
                    onChange={(value) => handleChange(question.id, value)}
                    disabled={timerExpired}
                    questionnaireInstanceId={questionnaireInstanceId}
                  />
                )}
              </div>
            </div>
          ))}
          
          <div className="flex justify-center w-full mt-10">
            <button
              id="submit-questionnaire-button"
              type="button"
              onClick={() => setShowSubmitConfirm(true)}
              disabled={isSubmitting}
              className={`text-white rounded-lg px-8 py-4 text-xl shadow-md hover:shadow-lg flex items-center ${
                isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transition-colors duration-200'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="inline-block animate-spin mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Questionnaire
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
          </form>
        )}
          </div>
          
          {/* Right Column - Sidebar - Only show when questionnaire is started */}
          {questionnaireStatus === 'qs_started' && (
            <div 
              className="w-80 py-10 px-8 rounded-r-3xl flex flex-col justify-between relative" 
              style={{ 
                backgroundColor: '#F9F9F9',
                backgroundImage: 'url(/leftbg.svg), url(/hotcircle.svg)',
                backgroundSize: '120% auto, auto',
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'top center, bottom center'
              }}
            >
              <div className="space-y-8">
                {/* Description section */}
                <div>
                  <h2 className="text-2xl font-regular text-gray-900 mb-4 leading-tight">
                    Help us understand your experience.
                  </h2>
                  <p className="text-gray-600 text-sm">
                    This questionnaire is ungraded and helps us learn more about your background.
                  </p>
                </div>

                {/* Status section */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Questionnaire Status
                  </h3>
                  <StatusPill status={questionnaireStatus} />
                </div>

                {/* Elapsed Time section */}
                <ElapsedTime startDate={startDate} />

                {/* Progress section */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Completion Progress
                  </h3>
                  <div className="flex justify-center">
                    <CompletionCircle 
                      completedQuestions={answeredQuestionsCount} 
                      totalQuestions={data?.questions?.length || 0} 
                    />
                  </div>
                </div>
              </div>

              {/* Bottom section with links and footer */}
              <div className="space-y-6">
                {/* Cheating monitoring warning */}
                <div className="rounded-lg p-3 mb-12 mt-8 relative" style={{ backgroundColor: '#FEF3C7' }}>
                  <div className="flex items-center">
                    <img 
                      src="/nocheatingicon.svg" 
                      alt="Fingers crossed" 
                      className="w-8 h-8 mr-3 flex-shrink-0"
                      style={{ filter: 'brightness(0) saturate(100%) invert(8%) sepia(50%) saturate(7500%) hue-rotate(0deg) brightness(95%) contrast(107%)' }}
                    />
                    <p className="text-sm text-left" style={{ color: '#7C0000' }}>
                      Integrity and authenticity checks are enabled.
                    </p>
                  </div>
                </div>
                {/* Action links */}
                <div className="flex justify-between items-center px-2">
                  <button
                    onClick={handleAbandonClick}
                    className="flex items-center text-red-600 hover:text-red-700 text-sm transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Abandon
                  </button>
                  
                  <button className="flex items-center text-gray-600 hover:text-gray-700 text-sm transition-colors duration-200">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Help
                  </button>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-300 pt-4">
                  <div className="text-xs text-gray-500 text-center">
                    Powered by tendent.ai
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Abandon Confirmation Modal */}
      <AbandonConfirmationModal
        isOpen={showAbandonConfirm}
        onCancel={handleCancelAbandon}
        onConfirm={handleConfirmAbandon}
        title="Abandon Questionnaire?"
        message="Are you sure you want to abandon this questionnaire? This action cannot be undone and your progress will be lost."
      />
      
      {/* Submit Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSubmitConfirm}
        onCancel={() => setShowSubmitConfirm(false)}
        onConfirm={submitQuestionnaire}
        title="Submit Questionnaire?"
        message="Are you sure you want to submit your questionnaire? Please review your answers before submitting."
      />
    </div>
  );
}

export default Questionnaire;
